import React, { Component, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import find from 'lodash/find';
import values from 'lodash/values';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';

import { SIZE_BLOCK, STYLE_NEUTRAL } from '@eb/eds-button';
import { Tag } from '@eb/eds-tag';
import { Icon } from '@eb/eds-icon';
import { FilterChunky as FilterChunkySvg } from '@eb/eds-iconography';
import { ChevronDownChunky } from '@eb/eds-iconography';
import { ChevronUpChunky } from '@eb/eds-iconography';
import { Badge } from '@eb/eds-badge';

import { HAS_WINDOW } from '@eb/feature-detection';

import { useScrollWatcher } from '../../../hooks';

import {
    CATEGORY_FILTER,
    DATE_FILTER,
    FORMAT_FILTER,
    PRICE_FILTER,
    LANGUAGE_FILTER,
    CURRENCY_FILTER,
    TAG_BACKGROUND_COLOR,
    TAG_STYLE,
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    CLEAR_PRICE_FILTER,
    HEADER_HEIGHT,
} from '../../constants';

import BotFilters from '../filterPanel/BotFilters';

import './mobileFilterHeader.scss';

const MOBILE_FILTER_HEADER_VERT_OFFSET = 16;

const getRenderedFilters = ({
    date,
    format,
    price,
    category,
    language,
    currency,
    onClose,
    onActiveToggle,
    filterTypeToShow,
}) => {
    const filterObject = {
        [DATE_FILTER]: date || '',
        [CATEGORY_FILTER]: category || '',
        [FORMAT_FILTER]: format || '',
        [PRICE_FILTER]: price || CLEAR_PRICE_FILTER,
        [LANGUAGE_FILTER]: language || '',
        [CURRENCY_FILTER]: currency || '',
    };

    const filtersToShow = Object.keys(filterObject).sort((a, b) => {
        const valA =
            !filterObject[a] || filterObject[a] === CLEAR_PRICE_FILTER ? 1 : 0;
        const valB =
            !filterObject[b] || filterObject[b] === CLEAR_PRICE_FILTER ? 1 : 0;

        return valB - valA;
    });

    return filtersToShow.map((key) => {
        // Given an applied filters value, grab the appropriate
        // display value associated to that filter
        const dropdownVals =
            FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].values;
        const label = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].label;

        const foundFilter = find(
            dropdownVals,
            ({ value }) =>
                value === filterObject[key] ||
                (Array.isArray(filterObject[key]) &&
                    filterObject[key][0] === value),
        );

        const filterIsShowing = filterTypeToShow === key;
        const iconType = filterIsShowing ? (
            <ChevronUpChunky />
        ) : (
            <ChevronDownChunky />
        );

        let display;

        if (Array.isArray(filterObject[key])) {
            display = `${foundFilter.display}${
                filterObject[key].length > 1
                    ? ` + ${filterObject[key].length - 1}`
                    : ''
            }`;
        } else if (foundFilter) {
            display = foundFilter.display;
        } else {
            // This is for handling pick a date, since it is variable
            // it doesn't show up in the FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP array
            display = date;
        }

        let isClosable = true;

        if (!filterObject[key]) {
            isClosable = false;
        }

        if (key === PRICE_FILTER && !price) {
            isClosable = false;
        }

        return (
            <span
                className={`facet facet--${key} eds-l-pad-left-1`}
                key={key}
                data-integration="facet"
            >
                <Tag
                    initialIsActive={false}
                    key={key}
                    isClosable={isClosable}
                    isStatic={isClosable}
                    backgroundColor={
                        isClosable ? TAG_BACKGROUND_COLOR : undefined
                    }
                    onClose={onClose.bind(null, key)}
                    onActiveToggle={() => onActiveToggle(key)}
                    style={TAG_STYLE}
                >
                    <span className="tag-content">
                        <span className="eds-l-pad-right-1">
                            {isClosable ? display : label}
                        </span>
                        {!isClosable && (
                            <Icon
                                type={iconType}
                                color="grey-700"
                                size="small"
                            />
                        )}
                    </span>
                </Tag>
            </span>
        );
    });
};

const MoreFiltersToggle = ({ onClick, spacingClass, filters }) => {
    const btnProps = {
        onClick,
        style: STYLE_NEUTRAL,
        size: SIZE_BLOCK,
    };

    const numberActiveFilters = uniq(compact(values(filters))).length;
    const isFilterActive = !!numberActiveFilters;

    return (
        <span
            className={`${spacingClass} more-filters`}
            data-integration="more-filters-facet"
        >
            <Tag
                initialIsActive={false}
                onActiveToggle={btnProps.onClick}
                backgroundColor={
                    isFilterActive ? TAG_BACKGROUND_COLOR : undefined
                }
                style={TAG_STYLE}
            >
                <span className="tag-content eds-text-weight--heavy">
                    <Badge
                        count={numberActiveFilters}
                        bgColor="white"
                        contentColor="ui-blue"
                    >
                        <Icon
                            type={<FilterChunkySvg />}
                            color={isFilterActive ? 'white' : 'grey-700'}
                            size="small"
                        />
                    </Badge>
                </span>
            </Tag>
        </span>
    );
};

class MobileFilterHeader extends Component {
    static propTypes = {
        trackGAEvent: PropTypes.func,
        handleApplyFilter: PropTypes.func,
        handleRemoveFilter: PropTypes.func,
        selectedCategory: PropTypes.string,
        selectedPrice: PropTypes.string,
        selectedDate: PropTypes.string,
        selectedFormat: PropTypes.string,
        isBotRequest: PropTypes.bool,
        locationSlug: PropTypes.string,
        facets: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                count: PropTypes.number,
                displayName: PropTypes.string,
            }),
        ),
        isLoading: PropTypes.bool,
        showFocusDrawer: PropTypes.func,
        closeFocusDrawer: PropTypes.func,
        addFocusDrawerNotification: PropTypes.func,
        hideFocusDrawerNotification: PropTypes.func,
        /**
         * Callback to execute when a filter is selected
         */
        openCloseAnimatedDrawer: PropTypes.func,
        /**
         * Type of individual filter active in the filter list
         */
        filterTypeToShow: PropTypes.string,
    };

    _handleRemoveFilter(filterType) {
        const { handleRemoveFilter } = this.props;

        handleRemoveFilter({ [filterType]: true });
    }

    render() {
        const {
            selectedDate,
            selectedCategory,
            selectedPrice,
            selectedFormat,
            openCloseAnimatedDrawer,
            filterTypeToShow,
            selectedLanguage,
            selectedCurrency,
            followedOrganizers,
            selectedOrganizations,
            selectedHashA,
        } = this.props;

        const handleOpenFilterPanel = (filterType) =>
            openCloseAnimatedDrawer(true, filterType);

        const appliedFiltersAsFacets = getRenderedFilters({
            date: selectedDate,
            format: selectedFormat,
            price: selectedPrice,
            category: selectedCategory,
            language: selectedLanguage,
            currency: selectedCurrency,
            onClose: this._handleRemoveFilter.bind(this),
            onActiveToggle: handleOpenFilterPanel,
            filterTypeToShow: filterTypeToShow,
        });

        const filters = {
            selectedCategory,
            selectedDate,
            selectedFormat,
            selectedPrice,
            selectedCurrency,
            selectedLanguage,
            followedOrganizers,
            selectedOrganizations,
            selectedHashA,
        };

        return (
            <>
                <MoreFiltersToggle
                    onClick={() => openCloseAnimatedDrawer(true)}
                    spacingClass="eds-show-down-sw"
                    filters={filters}
                />
                {[...appliedFiltersAsFacets]}
            </>
        );
    }
}

const MobileFilterHeaderContainer = (props) => {
    const { isBotRequest, locationSlug } = props;
    const classes = classNames('mobile-filter-header');
    const scrollablePaneClasses = classNames(
        'mobile-filter-header__facets',
        // Give scrollbar some space
        'eds-l-pad-bot-4',
    );

    return (
        <section className={classes} data-spec="mobile-filter-header">
            <div className={scrollablePaneClasses}>
                {isBotRequest ? (
                    <BotFilters slug={locationSlug} />
                ) : (
                    <MobileFilterHeader {...props} />
                )}
            </div>
        </section>
    );
};

const MobileFilterHeaderWithScrollWatcher = (props) => {
    let isMobile = true;

    if (HAS_WINDOW) {
        const mediaQuery = window.matchMedia('(min-width: 822px)');
        isMobile = !mediaQuery.matches;
    }

    const [isSticky, setIsSticky] = useState(false);
    const [
        { componentYOffset, componentHeight },
        setComponentDimensions,
    ] = useState({});

    // Generate ref on the search bar to determine how
    // much of an offset should be generated based on the
    // amount of content above it.
    const facetedSearchBar = useCallback((node) => {
        if (node !== null && node.getBoundingClientRect) {
            if (!componentYOffset) {
                const dimensions = node.getBoundingClientRect();

                setComponentDimensions({
                    componentYOffset:
                        node.offsetTop -
                        MOBILE_FILTER_HEADER_VERT_OFFSET +
                        HEADER_HEIGHT,
                    componentHeight: dimensions.height,
                });
            }
        }
    });

    useScrollWatcher(
        (pos) => {
            const shouldStick = pos > componentYOffset;
            if (isSticky !== shouldStick) {
                setIsSticky(shouldStick);
            }
        },
        {
            deps: [isSticky, componentYOffset],
            interval: 10,
        },
    );

    return isMobile && !props.isBotRequest ? (
        <div style={{ paddingTop: isSticky ? `${componentHeight}px` : '0' }}>
            <div
                ref={facetedSearchBar}
                className={`${isSticky ? 'mobile-filter-header--sticky' : ''}`}
            >
                <MobileFilterHeaderContainer {...props} />
            </div>
        </div>
    ) : (
        <MobileFilterHeaderContainer {...props} />
    );
};

export default MobileFilterHeaderWithScrollWatcher;
